import * as i18n from 'CMS/core/i18n';
import { formatDate } from 'visuris/Data/Formatters';
import RouteSegment from '../Data/Entities/RouteSegment';
import RouteEvent from '../Gis/RouteCalculatorDTOs/RouteEvent';
import * as RefData from 'AWFRefData/RefData';

export namespace VoyageResultHelper {
    const ErrorTranslationIds: { [reason: string]: string } = {
        'NoRoute': 'voyagecalculator.result.NoRoute2',
        'ShipDimensions': 'voyagecalculator.result.ShipDimensions2',
        'Blocked': 'voyagecalculator.result.Blocked2',
        'NoStartAndEnd': 'voyagecalculator.result.NoStartAndEnd',
        'StartNotFound': 'voyagecalculator.result.StartNotFound',
        'EndNotFound': 'voyagecalculator.result.EndNotFound',
        'ViaPointNotFound': 'voyagecalculator.result.ViaPointNotFound',
        'NoTimeProvided': 'voyagecalculator.result.NoTimeProvided',
        'BothTimesProvided': 'voyagecalculator.result.BothTimesProvided',
        'HistoricHorizon': 'voyagecalculator.result.HistoricHorizon',
        'FutureHorizon': 'voyagecalculator.result.FutureHorizon',
        'NoInput': 'voyagecalculator.result.NoInput',
        'UnexpectedError': 'voyagecalculator.result.UnexpectedError'
    };

    const ManeuverTranslationIds: { [maneuverType: string]: string } = {
        'Straight': 'voyageCalculator.bearing.straight',
        'BearRight': 'voyageCalculator.bearing.bearRight',
        'TurnRight': 'voyageCalculator.bearing.turnRight',
        'SharpRight': 'voyageCalculator.bearing.sharpRight',
        'UTurn': 'voyageCalculator.bearing.uTurn',
        'SharpLeft': 'voyageCalculator.bearing.sharpLeft',
        'TurnLeft': 'voyageCalculator.bearing.turnLeft',
        'BearLeft': 'voyageCalculator.bearing.bearLeft',
        'BorderCrossing': 'voyageCalculator.bearing.borderCrossing',
        'ChargesStart': 'voyageCalculator.event.chargesStart',
        'ChargesEnd': 'voyageCalculator.event.chargesEnd'
    };

    export function getNtSMessage(properties: any): string {
        let result: string = null;
        let restriction: string = null;
        const ntsType = properties['NtSType'];
        const ntsStartTime = properties['NtSStartTime'];
        switch (ntsType) {
            case 'DLY': {
                const ntsValue = properties['NtSValue'];
                result = i18n.getMessage(ntsStartTime ? 'voyagecalculator.event.DelayStart' : 'voyagecalculator.event.DelayEnd', ntsValue);
                break;
            }
            case 'DRA': {
                restriction = i18n.getMessage('voyagecalculator.event.RestrictionDraught');
                break;
            }
            case 'HEI': {
                restriction = i18n.getMessage('voyagecalculator.event.RestrictionHeight');
                break;
            }
            case 'LEN': {
                restriction = i18n.getMessage('voyagecalculator.event.RestrictionLength');
                break;
            }
            case 'WID': {
                restriction = i18n.getMessage('voyagecalculator.event.RestrictionWidth');
                break;
            }
            case 'SPR': {
                restriction = i18n.getMessage('voyagecalculator.event.RestrictionSpeed');
                break;
            }
            case 'TOT': {
                result = i18n.getMessage(ntsStartTime ? 'voyagecalculator.event.TotalObstructionStart' : 'voyagecalculator.event.TotalObstructionEnd');
                break;
            }
            default: {
                result = i18n.getMessage('voyagecalculator.event.Obstruction');
                break;
            }
        }
        if (restriction) {
            const ntsValue = properties['NtSValue'];
            if (ntsType === 'SPR') {
                result = i18n.getMessage(ntsStartTime ? 'voyagecalculator.event.RestrictionSpeedStart' : 'voyagecalculator.event.RestrictionSpeedEnd', restriction, ntsValue);
            } else {
                result = i18n.getMessage(ntsStartTime ? 'voyagecalculator.event.RestrictionStart' : 'voyagecalculator.event.RestrictionEnd', restriction, ntsValue);
            }
        }
        if (ntsStartTime) {
            result += ' ';
            const ntsEndTime = properties['NtSEndTime'];
            const startLocal = formatDate(new Date(ntsStartTime));
            if (!ntsEndTime) {
                result += i18n.getMessage('voyagecalculator.event.From', startLocal);
            } else {
                const endLocal = formatDate(new Date(ntsEndTime));
                result += i18n.getMessage('voyagecalculator.event.Between', startLocal, endLocal);
            }
        }
        const ntsNumber = properties['NtSNumber'];
        result += ' (' + ntsNumber + ')';
        return result.charAt(0).toUpperCase() + result.slice(1);
    }

    /**
     * Returns the segment message of NtsImpact maneuvertype
     * @param segmentName
     */
    export function getNtsSegmentMessage(segmentProperties: { [key: string]: string }): string {
        const message = getNtSMessage(segmentProperties);
        return message;
    }


    /**
     * Returns the translated event message for result from the VoyageCalculator
     * @param routeEvent
     * @returns
     */
    export function getEventMessage(routeEvent: RouteEvent): string {
        if (routeEvent.eventType === 'Vpln' || routeEvent.eventType === 'Bridge' || routeEvent.eventType === 'Lock') {
            return routeEvent.objectName ?? routeEvent.isrs;
        }
        if (routeEvent.eventType === 'BorderPoint' || routeEvent.eventType === 'Rdocal') {
            const eventTypeId = 'voyagecalculator.event.' + routeEvent.eventType;
            return `${i18n.getMessage(eventTypeId)} ${i18n.getMessage('voyagecalculator.event.At')} ${routeEvent.objectName ?? routeEvent.isrs}`;
        }
        if (routeEvent.eventType === 'TidalStart') {
            return `${i18n.getMessage('voyagecalculator.event.TidalWindow')}: ${routeEvent.objectName}`;
        }
        if (routeEvent.eventType.startsWith('NtsImpact')) {
            return getNtSMessage(routeEvent.eventProperties);
        }
        return routeEvent.eventMessage;
    }

    /**
     * Returns the translated segment message for result from the VoyageCalculator
     * @param maneuverType
     * @param segmentName
     * @param direction
     * @param index
     * @returns
     */
    export function getSegmentMessage(maneuverType: string, segmentName: string, direction: string, index: number = 1, segmentProperties: { [key: string]: string } = null): string {
        if (maneuverType === 'BorderCrossing') {
            return getSegmentBorderCrossingMessage(segmentName);
        }
        if (maneuverType === 'ChargesStart') {
            return i18n.getMessage('voyagecalculator.event.chargesstart', segmentName);
        }
        if (maneuverType === 'ChargesEnd') {
            return i18n.getMessage('voyagecalculator.event.chargesend');
        }
        if (maneuverType.startsWith('NtsImpact')) {
            return getNtsSegmentMessage(segmentProperties);
        }

        let s1, s2;
        if (maneuverType === 'Straight' && index === 0) {
            // first segment writes the direction instead of maneuver
            let directionId = null;
            if (direction === 'DOWNSTREAM') {
                directionId = 'voyagecalculator.direction.downstream';
            } else if (direction === 'UPSTREAM') {
                directionId = 'voyagecalculator.direction.upstream';
            }
            s1 = directionId !== null ? i18n.getMessage(directionId) : '';
        } else if (maneuverType !== null) {
            const messageId = ManeuverTranslationIds[maneuverType];
            s1 = i18n.getMessage(messageId);
        } else {
            s1 = null;
        }

        if (maneuverType !== 'Straight' && maneuverType !== 'UTurn') {
            s2 = i18n.getMessage('voyagecalculator.bearing.towards');
        } else {
            s2 = i18n.getMessage('voyagecalculator.bearing.on');
        }

        let result = s2 ? `${s1} ${s2} ${segmentName}` : `${s1} ${segmentName}`;
        result = result.trim();
        result = result.charAt(0).toUpperCase() + result.slice(1);

        if (maneuverType === 'Straight' && index > 0) {
            const directionId = direction === 'DOWNSTREAM' ? 'voyagecalculator.direction.downstream' : 'voyagecalculator.direction.upstream';
            const directionMessage = i18n.getMessage(directionId);
            result += ` (${directionMessage})`;
        }
        return result;
    }

    /**
     * Return the translated border crossing segment title by specified segment name
     * @param segmentName
     * @returns
     */
    export function getSegmentBorderCrossingMessage(segmentName: string): string {
        const borders = segmentName.split(' > ');
        let result;
        if (borders.length === 2) {
            let s1, s2;
            const commonBorder = i18n.getMessage('voyagecalculator.direction.CommonBorder');
            const entering = i18n.getMessage('voyagecalculator.direction.Entering');
            const exiting = i18n.getMessage('voyagecalculator.direction.Exiting');
            if (borders[1].split('-').length > 1) {
                s1 = `${entering} ${commonBorder} ${borders[1]}`;
            } else {
                s1 = `${entering} ${borders[1]}`;
            }
            const exitBordersLength = borders[0].split('-').length;
            if (exitBordersLength > 2) {
                s2 = null;
            } else if (exitBordersLength == 2) {
                s2 = `${exiting} ${commonBorder} ${borders[0]}`;
            } else {
                s2 = `${exiting} ${borders[0]}`;
            }
            result = s2 ? `${s1} & ${s2}` : s1;
        } else {
            result = i18n.getMessage('voyagecalculator.bearing.BorderCrossing') + ' ' + segmentName;
        }
        result = result.trim();
        result = result.charAt(0).toUpperCase() + result.slice(1);
        return result;
    }

    export function getDepartureOrArrivalMessage(objectName: string, defaultMessage: string, isDeparture: boolean = true): string {
        let translationId = isDeparture ? 'voyagecalculator.direction.DepartureFrom' : 'voyagecalculator.direction.ArrivalAt';
        return objectName ? `${i18n.getMessage(translationId)} ${objectName}` : defaultMessage;
    }

    /**
     * Return the translated route segment message for Eristat viewer, if needed properties are available
     * Otherwise returns the default description
     * @param segment
     * @returns
     */
    export function getRouteSegmentMessage(segment: RouteSegment): string {
        // check using the icon instead of role, because NtS message is stored in the role property for older route segments
        if (segment.icon.startsWith('NtS') || segment.icon.startsWith('NtsImpact')) {
            return segment.segmentProperties['NtSType'] ? getNtSMessage(segment.segmentProperties) : segment.description;
        }
        if (segment.icon.endsWith('Depart')) {
            const objectName = segment.segmentProperties['ObjectName'];
            return getDepartureOrArrivalMessage(objectName, segment.description);
        }
        if (segment.icon === 'Arrive' || segment.icon === 'esriDMTStop') {
            const objectName = segment.segmentProperties['ObjectName'];
            return getDepartureOrArrivalMessage(objectName, segment.description, false);
        }
        if (segment.icon.endsWith('ChargesStart')) {
            let authorities = segment.segmentProperties['ObjectName'];
            return authorities ? `${i18n.getMessage('voyagecalculator.event.chargesstart', authorities)}` : segment.description;
        }
        if (segment.icon.endsWith('ChargesEnd')) {
            return i18n.getMessage('voyagecalculator.event.ChargesEnd');
        }
        if (segment.icon.endsWith('Straight') || segment.icon.endsWith('Left') || segment.icon.endsWith('Right') || segment.icon.endsWith('UTurn')) {
            const dirId = 'voyagecalculator.bearing.' + segment.icon.replace('esriDMT', '');
            const prepositionId = segment.icon.endsWith('Straight') || segment.icon.endsWith('UTurn') ? 'voyagecalculator.bearing.towards' : 'voyagecalculator.bearing.on';
            const name = segment.segmentProperties['ObjectName'] ?? segment.fairwayName;
            return name ? `${i18n.getMessage(dirId)} ${i18n.getMessage(prepositionId)} ${name}` : segment.description;
        }
        if (segment.icon === 'BorderCrossing') {
            const segmentName = segment.segmentProperties['ObjectName'];
            return getSegmentBorderCrossingMessage(segmentName);
        }
        return `${i18n.getMessage('VisuRIS.Directions.Passing')} ${segment.segmentProperties['ObjectName'] ?? segment.isrs}`;
    }

    export function getErrorMessageFromReasonAndTags(reason: string, tags: any, objectName: string): string {
        const messageId = ErrorTranslationIds[reason];

        switch (reason) {
            case 'StartNotFound':
            case 'EndNotFound':
            case 'ViaPointNotFound': {
                let argument = tags['ISRS'];
                if (objectName) {
                    argument = `${objectName} (${argument})`;
                }
                return i18n.getMessage(messageId, argument);
            }
            case 'FutureHorizon': {
                const date = formatDate(new Date(tags['DepartureTime']));
                return i18n.getMessage(messageId, date, tags['FutureHorizon']);
            }
            case 'HistoricHorizon': {
                const date = formatDate(new Date(tags['DepartureTime']));
                return i18n.getMessage(messageId, date, tags['HistoricHorizon']);
            }
            case 'Blocked':
            case 'ShipDimensions': {
                const blockage = i18n.getMessage('voyagecalculator.event.Obstruction');
                const width = i18n.getMessage('voyagecalculator.event.RestrictionWidth');
                const height = i18n.getMessage('voyagecalculator.event.RestrictionHeight');
                const length = i18n.getMessage('voyagecalculator.event.RestrictionLength');
                const draught = i18n.getMessage('voyagecalculator.event.RestrictionDraught');
                let errorTag: string = tags[reason == 'Blocked' ? 'BlockedMessages' : 'DimensionMessages'];
                errorTag = errorTag.replaceAll('blockage', blockage).replaceAll('width', width).replaceAll('height', height).replaceAll('length', length).replaceAll('draught', draught);
                errorTag = convertCentimeterValuesToMeter(errorTag);
                return i18n.getMessage(messageId, errorTag);
            }
            case 'UnexpectedError': {
                return i18n.getMessage(messageId, tags['LogId']);
            }
            default: {
                return i18n.getMessage(messageId);
            }
        }
    }

    export function convertCentimeterValuesToMeter(message: string): string {
        const regex = /\b(\d+)cm\b/g;
        const unit = i18n.getMessage('units.meters');
        let match;
        let result = message;

        // global flag updates the lastIndex property, so it will do one match at a time, 
        // which is more memory efficient when the specified string is very large
        while ((match = regex.exec(message)) !== null) {
            const cmValue = parseInt(match[1]);
            let mValue = cmValue / 100;
            mValue = Math.round(mValue * 100) / 100; // round to 2 decimal places
            result = result.replace(match[0], mValue + unit);
        }

        return result;
    }

    /**
     * Get a unique consistent color from the combination of country codes
     * @param str
     * @returns
     */
    export function getColorFromString(str: string): string {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            // The country codes will always be between A - Z with character code between 65 - 90
            // Use it as percentage of 255 in order to generate a better color
            hash = Math.floor((90 - str.charCodeAt(i)) / 26 * 255) + ((hash << 5) - hash);
        }
        let color = '#';
        for (let i = 0; i < 3; i++) {
            let value = (hash >> (i * 8)) & 0xFF;
            color += ('00' + value.toString(16)).slice(-2);
        }
        return color;
    }
}